import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  FormGroup,
  Label,
  Input,
  Alert
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";

const apiUrl = process.env.REACT_APP_API_URL;

const Commission = (props) => {
  const { t } = props;
  const [commissionData, setCommissionData] = useState([]);
  const [managerCommissionData, setManagerCommissionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingManager, setLoadingManager] = useState(false);
  const [startDate, setStartDate] = useState(getLastTuesday());
  const [endDate, setEndDate] = useState(getNextMonday(startDate));
  const [userEmail, setUserEmail] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userId, setUserId] = useState("");
  const [totalSummary, setTotalSummary] = useState({
    frontCommission: 0,
    backCommission: 0,
    addonsTotal: 0,
    clawBack: 0,
    total: 0
  });
  const [managerTotalCommission, setManagerTotalCommission] = useState(0);

  // Function to get last Tuesday
  function getLastTuesday() {
    const today = new Date();
    const day = today.getDay(); // 0 is Sunday, 1 is Monday, 2 is Tuesday...
    const diff = (day <= 2) ? (day + 5) : (day - 2);
    const lastTuesday = new Date(today);
    lastTuesday.setDate(today.getDate() - diff);
    return lastTuesday;
  }

  // Function to get next Monday from a given date
  function getNextMonday(date) {
    const next = new Date(date);
    next.setDate(date.getDate() + 6); // Tuesday + 6 days = Monday
    return next;
  }

  // Format date to YYYY-MM-DD for API calls and inputs
  const formatDateForApi = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Get the user's email and role on component mount
  useEffect(() => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      if (authUser) {
        setUserEmail(authUser.email || "");
        setUserRole(authUser.role || "");
        setUserId(authUser.id || "");
      }
    } catch (error) {
      console.error("Error getting user data:", error);
      // Set fallback values for development
      setUserEmail("current.user@example.com");
      setUserRole("seller");
      setUserId("1");
    }
  }, []);

  // Calculate row total - ensure all values are numbers
  const calculateRowTotal = (record) => {
    const frontCommission = parseFloat(record.frontCommission) || 0;
    const backCommission = parseFloat(record.backCommission) || 0;
    const addonsTotal = parseFloat(record.addons_total) || 0;
    const clawBack = parseFloat(record.clawBack) || 0;
    
    return (frontCommission + backCommission + addonsTotal - clawBack).toFixed(2);
  };

  // Create memoized fetchCommissionData function with useCallback
  const fetchCommissionData = useCallback(async () => {
    if (!userEmail) return; // Don't fetch if no user email

    setLoading(true);
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const formattedStartDate = formatDateForApi(startDate);
      const formattedEndDate = formatDateForApi(endDate);
      
      const url = `${apiUrl}/api/data/reconciliation?start_date=${formattedStartDate}&end_date=${formattedEndDate}&seller_email=${encodeURIComponent(userEmail)}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch commission data");

      const data = await response.json();
      
      // Extract commission data from the response
      const commissionData = data.commissions || [];
      
      // Ensure all numeric fields are properly parsed to numbers
      const enhancedData = commissionData.map(record => ({
        ...record,
        frontCommission: parseFloat(record.frontCommission) || 0,
        backCommission: parseFloat(record.backCommission) || 0,
        addons_total: parseFloat(record.addons_total) || 0,
        clawBack: parseFloat(record.clawBack) || 0,
        rate: parseFloat(record.rate) || 0,
        total: calculateRowTotal(record)
      }));
      
      setCommissionData(enhancedData);
      
      // Use totals from the API response if available, otherwise calculate from data
      if (data.totals) {
        setTotalSummary({
          frontCommission: parseFloat(data.totals.totalFrontCommission) || 0,
          backCommission: parseFloat(data.totals.totalBackCommission) || 0,
          addonsTotal: parseFloat(data.totals.totalAddons) || 0,
          clawBack: parseFloat(data.totals.totalClawBack) || 0,
          total: parseFloat(data.totals.totalNet) || 0
        });
      } else {
        // Fallback to calculating from the data if totals not provided
        calculateSummaryTotals(enhancedData);
      }
    } catch (error) {
      console.error("Error fetching commission data:", error);
      // Use empty data in case of error
      setCommissionData([]);
      setTotalSummary({
        frontCommission: 0,
        backCommission: 0,
        addonsTotal: 0,
        clawBack: 0,
        total: 0
      });
    } finally {
      setLoading(false);
    }
  }, [apiUrl, startDate, endDate, userEmail]); // All dependencies explicitly listed

  // Create memoized fetchManagerCommissionData function with useCallback
  const fetchManagerCommissionData = useCallback(async () => {
    // Only fetch manager commission data if user is a seller_manager
    if (!userId || userRole !== 'seller_manager') return;

    setLoadingManager(true);
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const token = authUser?.token;
      if (!token) throw new Error("Authentication token not found");

      const formattedStartDate = formatDateForApi(startDate);
      const formattedEndDate = formatDateForApi(endDate);
      
      const url = `${apiUrl}/api/data/manager-commissions?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch manager commission data");

      const data = await response.json();
      
      // Extract manager commission data from the response
      const managerCommissions = data.managerCommissions || [];
      
      // Process and enhance the data
      const enhancedData = managerCommissions.map(record => ({
        ...record,
        commissionAmount: parseFloat(record.commissionAmount) || 0,
        percentageApplied: parseFloat(record.percentageApplied) || 0,
        saleAmount: parseFloat(record.saleAmount) || 0
      }));
      
      setManagerCommissionData(enhancedData);
      
      // Calculate total manager commission amount
      const total = enhancedData.reduce((sum, item) => sum + parseFloat(item.commissionAmount || 0), 0);
      setManagerTotalCommission(total);
    } catch (error) {
      console.error("Error fetching manager commission data:", error);
      setManagerCommissionData([]);
      setManagerTotalCommission(0);
    } finally {
      setLoadingManager(false);
    }
  }, [apiUrl, startDate, endDate, userId, userRole]); // All dependencies explicitly listed

  // Calculate summary totals (used as fallback if API doesn't provide totals)
  const calculateSummaryTotals = (data) => {
    const summary = data.reduce((acc, record) => {
      // Ensure all values are numeric before adding
      acc.frontCommission += parseFloat(record.frontCommission) || 0;
      acc.backCommission += parseFloat(record.backCommission) || 0;
      acc.addonsTotal += parseFloat(record.addons_total) || 0;
      acc.clawBack += parseFloat(record.clawBack) || 0;
      return acc;
    }, {
      frontCommission: 0,
      backCommission: 0,
      addonsTotal: 0,
      clawBack: 0
    });

    summary.total = summary.frontCommission + summary.backCommission + summary.addonsTotal - summary.clawBack;
    setTotalSummary(summary);
  };

  // Handle date range change (modified for native date inputs)
  const handleDateRangeChange = (e, isStart) => {
    const date = new Date(e.target.value);
    if (isStart) {
      setStartDate(date);
      setEndDate(getNextMonday(date));
    } else {
      setEndDate(date);
    }
  };

  // Load commission data when email is available or dates change
  useEffect(() => {
    if (userEmail) {
      fetchCommissionData();
    }
  }, [fetchCommissionData, userEmail]);

  // Load manager commission data if user is a seller_manager
  useEffect(() => {
    if (userRole === 'seller_manager') {
      fetchManagerCommissionData();
    }
  }, [fetchManagerCommissionData, userRole]);

  // Meta title
  document.title = "Commission | Sunwave";

  // Calculate total income (seller commissions + manager commissions)
  const totalIncome = parseFloat(totalSummary.total) + parseFloat(managerTotalCommission);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t("Commission")} breadcrumbItem={t("Commission")} />

          <Row className="mb-4">
            <Col lg="12">
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col md="6">
                      <FormGroup>
                        <Label>{t("Start Date (Tuesday)")}</Label>
                        <Input
                          type="date"
                          className="form-control"
                          value={formatDateForApi(startDate)}
                          onChange={(e) => handleDateRangeChange(e, true)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>{t("End Date (Monday)")}</Label>
                        <Input
                          type="date"
                          className="form-control"
                          value={formatDateForApi(endDate)}
                          onChange={(e) => handleDateRangeChange(e, false)}
                          min={formatDateForApi(startDate)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">{t("Summary")}</h4>
                  <Row>
                    <Col md={userRole === 'seller_manager' ? 2 : 3}>
                      <div className="mb-3">
                        <h5>{t("Front Commission")}</h5>
                        <h4>${(parseFloat(totalSummary.frontCommission) || 0).toFixed(2)}</h4>
                      </div>
                    </Col>
                    <Col md={userRole === 'seller_manager' ? 2 : 3}>
                      <div className="mb-3">
                        <h5>{t("Back Commission")}</h5>
                        <h4>${(parseFloat(totalSummary.backCommission) || 0).toFixed(2)}</h4>
                      </div>
                    </Col>
                    <Col md={userRole === 'seller_manager' ? 2 : 3}>
                      <div className="mb-3">
                        <h5>{t("Addons Total")}</h5>
                        <h4>${(parseFloat(totalSummary.addonsTotal) || 0).toFixed(2)}</h4>
                      </div>
                    </Col>
                    <Col md={userRole === 'seller_manager' ? 2 : 3}>
                      <div className="mb-3">
                        <h5>{t("Claw Back")} (subtracted)</h5>
                        <h4>-${(parseFloat(totalSummary.clawBack) || 0).toFixed(2)}</h4>
                      </div>
                    </Col>
                    {userRole === 'seller_manager' && (
                      <>
                        <Col md={2}>
                          <div className="mb-3">
                            <h5>{t("Manager Commission")}</h5>
                            <h4>${(parseFloat(managerTotalCommission) || 0).toFixed(2)}</h4>
                          </div>
                        </Col>
                        <Col md={2}>
                          <div className="mb-3">
                            <h5>{t("Total Income")}</h5>
                            <h4>${(parseFloat(totalIncome) || 0).toFixed(2)}</h4>
                          </div>
                        </Col>
                      </>
                    )}
                  </Row>
                  <Row>
                    <Col md="12">
                      <Alert color="success" className="mb-0">
                        {userRole === 'seller_manager' ? (
                          <h5 className="mb-0">
                            {t("Sales Total")}: ${(parseFloat(totalSummary.total) || 0).toFixed(2)} | 
                            {t("Manager Total")}: ${(parseFloat(managerTotalCommission) || 0).toFixed(2)} | 
                            {t("Total Income")}: ${(parseFloat(totalIncome) || 0).toFixed(2)}
                          </h5>
                        ) : (
                          <h5 className="mb-0">{t("Total")}: ${(parseFloat(totalSummary.total) || 0).toFixed(2)}</h5>
                        )}
                      </Alert>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">{t("Sales Commission Data")}</h4>
                  <div className="table-responsive">
                    <Table className="table table-centered table-nowrap mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>{t("ID")}</th>
                          <th>{t("Date Range")}</th>
                          <th>{t("Customer")}</th>
                          <th>{t("Size")}</th>
                          <th>{t("Card ID")}</th>
                          <th>{t("Rate")}</th>
                          <th>{t("Type")}</th>
                          <th>{t("Front Commission")}</th>
                          <th>{t("Back Commission")}</th>
                          <th>{t("Addons")}</th>
                          <th>{t("Claw Back")}</th>
                          <th>{t("Total")}</th>
                          <th>{t("Last Modified")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="13" className="text-center">
                              {t("Loading...")}
                            </td>
                          </tr>
                        ) : commissionData.length === 0 ? (
                          <tr>
                            <td colSpan="13" className="text-center">
                              {t("No records found")}
                            </td>
                          </tr>
                        ) : (
                          commissionData.map((record, index) => (
                            <tr key={index}>
                              <td>{record.id}</td>
                              <td>
                                {new Date(record.startDate).toLocaleDateString()} - {new Date(record.endDate).toLocaleDateString()}
                              </td>
                              <td>{record.customer}</td>
                              <td>{record.size}</td>
                              <td>{record.cardId}</td>
                              <td>${typeof record.rate === 'number' ? record.rate.toFixed(2) : '0.00'}</td>
                              <td>{t(record.type?.charAt(0).toUpperCase() + record.type?.slice(1) || '')}</td>
                              <td>${typeof record.frontCommission === 'number' ? record.frontCommission.toFixed(2) : '0.00'}</td>
                              <td>${typeof record.backCommission === 'number' ? record.backCommission.toFixed(2) : '0.00'}</td>
                              <td>{record.addons}</td>
                              <td>${typeof record.clawBack === 'number' ? record.clawBack.toFixed(2) : '0.00'}</td>
                              <td className="font-weight-bold">${typeof parseFloat(record.total) === 'number' && !isNaN(parseFloat(record.total)) ? parseFloat(record.total).toFixed(2) : '0.00'}</td>
                              <td>
                                {record.lastModified ? new Date(record.lastModified).toLocaleDateString() : ''} {record.modifiedBy || ''}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Manager Commission Table - Only shown for seller_manager role */}
          {userRole === 'seller_manager' && (
            <Row className="mt-4">
              <Col lg="12">
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">{t("Manager Commission Data")}</h4>
                    <div className="table-responsive">
                      <Table className="table table-centered table-nowrap mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th>{t("ID")}</th>
                            <th>{t("Date Range")}</th>
                            <th>{t("Seller")}</th>
                            <th>{t("Customer")}</th>
                            <th>{t("Card ID")}</th>
                            <th>{t("Sale Amount")}</th>
                            <th>{t("Commission Type")}</th>
                            <th>{t("Rate (%)")}</th>
                            <th>{t("Commission Amount")}</th>
                            <th>{t("Status")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingManager ? (
                            <tr>
                              <td colSpan="10" className="text-center">
                                {t("Loading...")}
                              </td>
                            </tr>
                          ) : managerCommissionData.length === 0 ? (
                            <tr>
                              <td colSpan="10" className="text-center">
                                {t("No manager commission records found")}
                              </td>
                            </tr>
                          ) : (
                            managerCommissionData.map((record, index) => (
                              <tr key={index}>
                                <td>{record.id}</td>
                                <td>
                                  {new Date(record.startDate).toLocaleDateString()} - {new Date(record.endDate).toLocaleDateString()}
                                </td>
                                <td>{record.sellerEmail}</td>
                                <td>{record.customer}</td>
                                <td>{record.cardId}</td>
                                <td>${(parseFloat(record.saleAmount) || 0).toFixed(2)}</td>
                                <td>{t(record.commissionType?.charAt(0).toUpperCase() + record.commissionType?.slice(1) || '')}</td>
                                <td>{record.percentageApplied}%</td>
                                <td>${(parseFloat(record.commissionAmount) || 0).toFixed(2)}</td>
                                <td>
                                  <span className={`badge badge-${record.status === 'active' ? 'success' : record.status === 'paid' ? 'primary' : 'warning'}`}>
                                    {record.status}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(Commission);